import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { MetaSearchOfferType } from 'actions/OfferActions'
import { fetchMetaOfferIds } from 'actions/OfferActions'
import { fetchMetaSearchOffer, getOfferWithDiscountKey, clearOffersWithDiscount } from 'actions/MarketingAction'
import getOfferListKey from 'lib/offer/offerListKey'
import type { SetOptional } from 'type-fest'
import { sortBy, without } from 'lib/array/arrayUtils'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { buildMetaSearchOfferListFilters } from 'lib/metasearch/metaSearchOfferListFilters'

export interface MetaSearchOfferListOptions {
  disabled?: boolean
  excludedIds?: Array<string>
  sortMode?: 'recommendation' | 'distance' | 'discount' | 'rating'
}

export interface MetaSearchOfferListItem {
  offerId: string
  type?: MetaSearchOfferType
  checkIn?: string
  checkOut?: string
  discountedPackages?: App.FeedOperations['getOfferWithDiscount']['responses']['200']['content']['application/json']['result']['discountedPackages']
}

export interface MetaSearchOfferListState {
  items: Array<MetaSearchOfferListItem>
  fetching: boolean
}

const emptyOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: false,
  error: null,
}

const emptyResult: Array<MetaSearchOfferListItem> = []

function useMetaSearchOfferList(
  filters: App.MetaSearchObject | undefined,
  options: MetaSearchOfferListOptions = {},
): MetaSearchOfferListState {
  const { excludedIds, sortMode = 'recommendation' } = options

  const searchDisabled = options.disabled || !filters
  const dispatch = useAppDispatch()

  const regionCode = useAppSelector((state) => state.geo.currentRegionCode)

  // Cleanup offersWithDiscount state when unmounting
  useEffect(() => {
    return () => {
      dispatch(clearOffersWithDiscount())
    }
  }, [dispatch])

  // Build App.OfferListFilters from MetaSearchObject — shared with SSR via
  // `buildMetaSearchOfferListFilters` to guarantee the offer-list key matches
  // the SSR-populated slice (otherwise the client silently re-fetches).
  const offerListFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!filters) return undefined
    return buildMetaSearchOfferListFilters(filters, searchDisabled)
  }, [filters, searchDisabled])

  const listKey = useMemo(() => {
    if (offerListFilters) {
      return getOfferListKey(offerListFilters)
    }
    return ''
  }, [offerListFilters])

  useEffect(() => {
    if (searchDisabled || !offerListFilters) return

    dispatch(fetchMetaOfferIds(offerListFilters))
  }, [listKey, searchDisabled, dispatch, offerListFilters])

  const offerList = useAppSelector((state) => {
    if (searchDisabled) {
      return emptyDisabledOfferList
    }
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  const offersWithDiscountState = useAppSelector((state) => state.offer.offersWithDiscount)
  const offersState = useAppSelector((state) => state.offer.offers)

  const metaSearchOfferTypes = offerList.metaSearchOfferTypes
  const metaSearchOfferDates = offerList.metaSearchOfferDates

  // Fetch offers with discount for pinned offers only (use server-provided default dates if available)
  useEffect(() => {
    if (searchDisabled || !filters || offerList.fetching || offerList.offerIds.length === 0) return

    offerList.offerIds.forEach((offerId) => {
      const offerType = metaSearchOfferTypes?.[offerId]
      if (offerType !== 'pinned') return

      const offerDates = metaSearchOfferDates?.[offerId]
      const checkIn = offerDates?.checkIn
      const checkOut = offerDates?.checkOut

      const key = getOfferWithDiscountKey(offerId, checkIn, checkOut)
      // Skip if already fetching or fetched for this key (offerId + dates)
      if (offersWithDiscountState[key]) return

      const params: { region: string, checkIn?: string, checkOut?: string } = { region: filters.region }
      if (checkIn) params.checkIn = checkIn
      if (checkOut) params.checkOut = checkOut

      dispatch(fetchMetaSearchOffer(offerId, params, regionCode))
    })
  }, [searchDisabled, filters, offerList.fetching, offerList.offerIds, metaSearchOfferTypes, metaSearchOfferDates, offersWithDiscountState, dispatch, regionCode])

  // Build the final sorted list with discounted packages
  const result = useMemo((): Array<MetaSearchOfferListItem> => {
    if (searchDisabled || offerList.fetching) return emptyResult

    // Apply exclusions
    const filteredIds = excludedIds ? without(offerList.offerIds, ...excludedIds) : offerList.offerIds

    // Build items with discount data and type
    const items: Array<MetaSearchOfferListItem> = filteredIds.map((offerId) => {
      const offerType = metaSearchOfferTypes?.[offerId]
      const offerDates = metaSearchOfferDates?.[offerId]
      const checkIn = offerDates?.checkIn
      const checkOut = offerDates?.checkOut

      const key = getOfferWithDiscountKey(offerId, checkIn, checkOut)
      // Only pinned offers have discounted packages
      const discountData = offerType === 'pinned' ? offersWithDiscountState[key] : undefined
      return {
        offerId,
        type: offerType,
        checkIn,
        checkOut,
        discountedPackages: discountData?.discountedPackages,
      }
    })

    // If no sorting needed, return as-is
    if (sortMode === 'recommendation') {
      return items
    }

    // Enrich with sorting data
    const mainLatitude = filters?.latitude
    const mainLongitude = filters?.longitude

    const enriched = items.map((item) => {
      const offer = offersState[item.offerId] as App.HotelOffer | undefined

      const offerLat = offer?.property?.latitude
      const offerLon = offer?.property?.longitude

      const distance = (
        typeof mainLatitude === 'number' &&
        typeof mainLongitude === 'number' &&
        typeof offerLat === 'number' &&
        typeof offerLon === 'number'
      )
        ? calculateDistance(mainLatitude, mainLongitude, offerLat, offerLon)
        : Number.POSITIVE_INFINITY

      const discount = offer?.lowestPricePackage?.defaultPricing?.discountPercent ?? 0
      const rating = offer?.property?.rating?.score ?? 0

      return { item, distance, discount, rating }
    })

    // Sort based on mode
    const sorted = sortBy(enriched, (a) => {
      if (sortMode === 'distance') return a.distance
      if (sortMode === 'discount') return a.discount
      if (sortMode === 'rating') return a.rating
      return 0
    }, sortMode === 'distance' ? 'asc' : 'desc')

    return sorted.map((e) => e.item)
  }, [searchDisabled, offerList.fetching, offerList.offerIds, excludedIds, metaSearchOfferTypes, metaSearchOfferDates, offersWithDiscountState, offersState, sortMode, filters?.latitude, filters?.longitude])

  return {
    items: result,
    fetching: offerList.fetching,
  }
}

export default useMetaSearchOfferList
